<template>
  <div id="app-layout" class="c-app-layout" :style="bottomHeightStyle">
    <PullToRefresh
      v-if="isPWA"
      v-model="pullToRefresh"
      @update:refresh="onRefresh"
    />

    <AppSystemBar :message="message" />

    <AppSideBar
      v-if="!isReallyMobile"
      :show="showMenu"
      @width="sideMenuWidth = $event"
    />

    <AppTopBar
      :menu="showMenu"
      :title="title"
      :subtitle="subtitle"
      :left="sideMenuWidth"
      @toggle="toggleMenu"
    />

    <AppMain @count="subtitle = `${$event}`" />

    <AppFooter v-if="showFooter" />

    <AppBottomBar
      v-if="isReallyMobile"
      :show="showMenu"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import AppSystemBar from '@/components/app/AppSystemBar'
import AppTopBar from '@/components/app/AppTopBar'
import AppMain from '@/components/app/AppMain'
import AppSideBar from '@/components/app/AppSideBar'
import AppBottomBar from '@/components/app/AppBottomBar'
import AppFooter from '@/components/app/AppFooter'
import PullToRefresh from '@/components/base/PullToRefresh'

import MessageService from '@/services/messageService.js'
import mobileMixin from '@/mixins/mobileMixin.js'
import pwaMixin from '@/mixins/pwaMixin.js'

export default {
  name: 'AppLayout',

  components: {
    AppSystemBar,
    AppTopBar,
    AppMain,
    AppSideBar,
    AppBottomBar,
    AppFooter,
    PullToRefresh
  },

  mixins: [mobileMixin, pwaMixin],

  data: function () {
    return {
      sideMenuWidth: 0,
      message: {},
      pullToRefresh: false,
      showMenu: true,
      showFooter: false,
      title: '',
      subtitle: ''
    }
  },

  computed: {
    ...mapGetters('menuStore', ['getMenuItemTitle']),
    ...mapGetters('userStore', ['getSetting']),

    locale() {
      return this.$store.state.i18nStore.locale
    },

    isMobile() {
      return this.mobileMixin_isMobile
    },

    isReallyMobile() {
      return this.mobileMixin_isReallyMobile
    },

    isPWA() {
      return this.pwaMixin_isPWA
    },

    bottomHeightStyle() {
      return {
        // prettier-ignore
        // return the width of the side menu
        '--c-side-menu-width': this.isReallyMobile
          ? '0px'
          : `${this.sideMenuWidth}px`,
        // return either the height of the mobile menu bar or the footer
        '--c-bottom-height': this.isReallyMobile
          ? 'var(--c-bottom-bar-height);'
          : 'var(--c-footer-height);'
      }
    },

    appName() {
      return this.portal.title?.[this.locale] || this.$appConfig.appName
    },

    orgName() {
      return this.$store.state.orgStore.orgName
    },

    portal() {
      return this.$store.state.portalStore.activePortal
    }
  },

  watch: {
    $route: {
      immediate: true,
      handler: function (to, _from) {
        this.title = this.getMenuItemTitle(to, this.locale)
        this.subtitle = '' // clear the count until the new route recalculates it
        document.title = this.isPWA ? this.title : `${this.title} | ${this.orgName}`
      }
    }
  },

  created: function () {
    this.messageService = new MessageService()

    this.messageService.fetchSystemMessage().then((message) => {
      if (Object.keys(message).length > 0) {
        this.message = message
        this.enabled = true
      }
    })

    this.showMenu = this.getSetting('showMenu')
  },

  activated: function () {
    this.subtitle = ''
  },

  methods: {
    ...mapActions('userStore', ['updateSettings']),
    ...mapActions(['reyncData']),

    async onRefresh() {
      if (this.pullToRefresh) {
        console.debug('[AppLayout]: refresh triggered!')
        await this.resyncData()
        this.pullToRefresh = false
      }
    },

    toggleMenu() {
      this.showMenu = !this.showMenu
      this.updateSettings({ showMenu: this.showMenu })
    }
  }
}
</script>

<style lang="css">
:root {
  --c-top-bar-height: 48px;
  --c-bottom-bar-height: 72px;
  --c-footer-height: 48px;
  --c-footer-height: 0px;
}

.c-app-layout {
  background-color: var(--v-sheet-base);
  height: 100%;
  overflow-y: visible;
}
</style>
